import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { getPostLookupError } from './post-lookup-error';

type PostLookup = {
  communityId?: string | null;
  communities?: Array<{ communityId?: string }> | null;
};

function extractCommunityId(post: PostLookup): string | null {
  if (post.communityId) return post.communityId;
  if (Array.isArray(post.communities) && post.communities[0]?.communityId) {
    return post.communities[0].communityId;
  }
  return null;
}

/**
 * Legacy deep-link resolver: /posts/:postId → /:communityId/posts/:postId
 * so the post renders inside the community dashboard shell.
 */
export default function PostPage() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const resolvePost = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/posts/${postId}`);
        const communityId = extractCommunityId(data);
        if (communityId) {
          navigate(`/${communityId}/posts/${postId}`, { replace: true });
        } else {
          setError('This post is not linked to a community');
        }
      } catch (err: unknown) {
        console.error(err);
        setError(getPostLookupError(err));
      }
    };

    if (postId) resolvePost();
  }, [postId, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 text-center">
        <h2 className="text-xl font-semibold font-outfit mb-4">{error}</h2>
        <Button
          onClick={() => navigate('/dashboard', { replace: true })}
          variant="outline"
          className="rounded-xl"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Go to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex justify-center items-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}
